import {
  FileInterface,
} from '@/shared/DesignSystem';
import IValidationMethod from './Contract/IValidationMethod';

import EProtocol from './Enum/EProtocol';

import lowerCaseProtocol from './Utils/lowerCaseProtocol';

class ValidationMethod implements IValidationMethod {
  customValidator(
    value: unknown,
    formData: unknown,
    callback: (
      value: unknown,
      formData?: unknown
    ) => boolean,
  ): boolean {
    return callback(value, formData);
  }

  emailValidator(
    value: string,
  ): boolean {
    return this.regexValidator(
      value,
      // eslint-disable-next-line max-len
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
  }

  fileValidator(
    value: FileInterface[],
  ): boolean {
    let hasValidFile = false;

    value.forEach((file) => {
      if (file.errors.length === 0) {
        hasValidFile = true;
      }
    });

    return hasValidFile;
  }

  matchValidator(
    value1: unknown,
    value2: unknown,
  ): boolean {
    if (typeof value1 !== typeof value2) {
      return false;
    }

    if (['number', 'string'].includes(typeof value1)) {
      return value1 === value2;
    }

    if (value1 === null || value2 === null || value1 === undefined || value2 === undefined) {
      return value1 === value2;
    }

    if (Array.isArray(value1) && Array.isArray(value2)) {
      const compareFirstWithSecond = value1.reduce((result, val, index) => {
        const preResult = this.matchValidator(
          value1[index] ?? null,
          value2[index] ?? null,
        );

        return result && preResult;
      }, true);
      const compareSecondWithFirst = value2.reduce((result, val, index) => {
        const preResult = this.matchValidator(
          value2[index] ?? null,
          value1[index] ?? null,
        );

        return result && preResult;
      }, true);
      return compareFirstWithSecond && compareSecondWithFirst;
    }

    const compareFirstWithSecond = Object.keys((value1 as object)).reduce((result, key) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const preResult = this.matchValidator((value1 as object)[key] ?? null, (value2 as object)[key] ?? null);

      return result && preResult;
    }, true);
    const compareSecondWithFirst = Object.keys((value2 as object)).reduce((result, key) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const preResult = this.matchValidator((value2 as object)[key] ?? null, (value1 as object)[key] ?? null);

      return result && preResult;
    }, true);

    return compareFirstWithSecond && compareSecondWithFirst;
  }

  private parseNumber(value: string | number | Date): number {
    let number: number = value as number;

    if (value instanceof Date) {
      number = value.getTime();
    } else if (typeof value === 'string') {
      number = Number.parseInt(value, 10);
    }

    return number;
  }

  greaterThanValidator(
    testedValue: unknown,
    targetValue: unknown,
  ): boolean {
    if (
      ((typeof testedValue === 'string' || typeof testedValue === 'number' || testedValue instanceof Date)
      && (typeof targetValue === 'string' || typeof targetValue === 'number' || targetValue instanceof Date))
    ) {
      return this.parseNumber(testedValue) > this.parseNumber(targetValue);
    }

    throw new Error('Value must be string, number or Date');
  }

  lessThanValidator(
    testedValue: unknown,
    targetValue: unknown,
  ): boolean {
    if (
      ((typeof testedValue === 'string' || typeof testedValue === 'number' || testedValue instanceof Date)
        && (typeof targetValue === 'string' || typeof targetValue === 'number' || targetValue instanceof Date))
    ) {
      return this.parseNumber(testedValue) < this.parseNumber(targetValue);
    }

    throw new Error('Value must be string, number or Date');
  }

  maxLengthValidator(
    value: string,
    length: number,
  ): boolean {
    return (value ?? '').length <= length;
  }

  minLengthValidator(
    value: string,
    length: number,
  ): boolean {
    return (value ?? '').length >= length;
  }

  notEmptyValidator(
    value: unknown,
  ): boolean {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return !!value.trim();
    }

    if (typeof value === 'object' && value.constructor?.name === 'Object') {
      return Object.values(value as object).length > 0;
    }

    return !!value;
  }

  onlyNumbersValidator(
    value: string,
  ): boolean {
    return this.regexValidator(
      value,
      /^\d+$/,
    );
  }

  regexValidator(
    value: string,
    pattern: string | RegExp,
  ): boolean {
    return value ? (new RegExp(pattern)).test(value) : true;
  }

  requiredValidator(
    value: unknown,
  ): boolean {
    return !(value === null || value === undefined);
  }

  urlValidator(
    value: string,
    protocols: EProtocol[],
  ): boolean {
    if (!protocols) {
      throw new Error('Protocols parameter must be set');
    }

    const protocolsString = protocols.join('|');
    // eslint-disable-next-line max-len
    const regex = new RegExp(`^(${protocolsString}):\\/\\/(((?:[_\\.\\p{L}\\p{N}-]|%%[0-9A-Fa-f]{2})+:)?((?:[_\\.\\p{L}\\p{N}-]|%%[0-9A-Fa-f]{2})+)@)?(([\\p{L}\\p{N}\\p{S}\\-_]+\\.)*(([\\p{L}\\p{N}]|xn--[\\p{L}\\p{N}-]+)+\\.?)|\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}|\\[(?:(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){6})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:::(?:(?:(?:[0-9a-f]{1,4})):){5})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:[0-9a-f]{1,4})))?::(?:(?:(?:[0-9a-f]{1,4})):){4})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,1}(?:(?:[0-9a-f]{1,4})))?::(?:(?:(?:[0-9a-f]{1,4})):){3})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,2}(?:(?:[0-9a-f]{1,4})))?::(?:(?:(?:[0-9a-f]{1,4})):){2})(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,3}(?:(?:[0-9a-f]{1,4})))?::(?:(?:[0-9a-f]{1,4})):)(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,4}(?:(?:[0-9a-f]{1,4})))?::)(?:(?:(?:(?:(?:[0-9a-f]{1,4})):(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9]))\\.){3}(?:(?:25[0-5]|(?:[1-9]|1[0-9]|2[0-4])?[0-9])))))))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,5}(?:(?:[0-9a-f]{1,4})))?::)(?:(?:[0-9a-f]{1,4})))|(?:(?:(?:(?:(?:(?:[0-9a-f]{1,4})):){0,6}(?:(?:[0-9a-f]{1,4})))?::))))\\])(:[0-9]+)?(?:\\/(?:[\\p{L}\\p{N}\\-._~!$&'()*+,;=:@]|%%[0-9A-Fa-f]{2})*)*(?:\\?(?:[\\p{L}\\p{N}\\-._~!$&'\\[\\]()*+,;=:@/?]|%%[0-9A-Fa-f]{2})+)?(?:#(?:[\\p{L}\\p{N}\\-._~!$&'()*+,;=:@/?]|%%[0-9A-Fa-f]{2})+)?$`, 'gmu');

    return this.regexValidator(
      lowerCaseProtocol(value),
      regex,
    );
  }
}

export default ValidationMethod;
